import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { Col, Container, Row } from 'react-bootstrap';
import { useAlert } from 'react-alert';
import Alert from '@mui/material/Alert';

import * as S from './styles';
import './checkout-style.scss';
import { IProps } from './types';
import { CredentialContext, credentialType } from '@src/providers';
import useIsComDomain from '@src/hooks/useIsComDomain';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import Logo from '@src/imagesNew/logo/maccaron-logo.jpg';
import GlobalStoreLogo from '@src/imagesNew/logo/global-store-logo.png';
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import { ImageButton, Loading } from '@atoms';
import { CheckoutInformation, CheckoutSummary } from '@organisms';
import { CheckoutAlert } from '@components/CheckoutAlert';
import { OtpVerificationModal } from '@molecules/OtpVerificationModal';
import MGSDeliveryModal from '@src/componentsNew/Modals/MGSDeliveryModal/MGSDeliveryModal';

import { CHECKOUT, CHECKOUT_COMPLETE, CHECKOUT_UPDATE, USER_MOBILE } from '@components/pages/CheckoutPage/queries';
import { userMobile } from '@components/pages/CheckoutPage/__generated__/userMobile';
import {
  checkout as checkoutType,
  checkout_subcarts_lines,
} from '@components/pages/CheckoutPage/__generated__/checkout';
import { checkoutUpdate } from '@components/pages/CheckoutPage/__generated__/checkoutUpdate';
import { checkoutQuery } from '@components/pages/CheckoutPage/__generated__/checkoutQuery';
import { checkoutComplete } from '@components/pages/CheckoutPage/__generated__/checkoutComplete';
import { OrderPaymentType } from '../../../../__generated__/globalTypes';
import { ME_EMAIL_VERIFIED } from '@src/app/queries';
import { meEmailVerified } from '@src/app/__generated__/meEmailVerified';
import { tokenCreate_tokenCreate } from '@src/providers/CredentialProvider/__generated__/tokenCreate';

import {
  gaV4BeginCheckout,
  gaV4GetItemsFromCart,
  gaV4Purchase,
  iterateCartLines,
  mergeMessagesAndErrors,
} from '@src/core/utils';

import { RESOLVE_ERROR_MESSAGE } from '@src/core/config';
import { pixelInitiateCheckout, pixelPurchase } from '@src/utils/pixel';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';
import { baseUrl, orderUrl, emailVerifyRequestUrl } from '@src/app/routes';
import { isInvalidShippingAddress } from '@src/utils/shippingAddressValidation';

type CheckoutError = {
  level: string;
  message: string;
};

const CheckoutPage: React.FC<IProps> = ({}: IProps) => {
  const history = useHistory();
  const isComDomain = useIsComDomain();
  const { currentUser, currentCart, setCurrentCart, setCurrentUser } = useContext<credentialType>(CredentialContext);
  const { showSnackbar } = useSnackbar();

  const alert = useAlert();
  const token = currentUser?.token;
  if (!token) {
    redirectToLoginWithCurrentPath(history);
  }

  const [showMGSDeliveryModal, setShowMGSDeliveryModal] = useState(false);
  const [checkout, setCheckout] = useState<checkoutType>(null);

  const calculateGrandTotal = (checkout: checkoutType) => {
    /* grandTotal Calculation Source:
https://github.com/velymonkeys/velymonkeys/blob/20275492f01dd22dd5c31dd2fc930d9f9f90b558/saleor/
mp_checkout/models.py#L186 */
    if (checkout === null) {
      alert.show('Something wrong! Redirecting to Home in 5 seconds.', {
        onClose: () => {
          window.location.href = baseUrl;
        },
        timeout: 5000,
        type: 'error',
      });
      return;
    }
    const calculatedGrandTotal =
      checkout.totalPrice.gross.amount +
      checkout.shippingPrice.amount +
      checkout.codAmount.amount -
      checkout.discountAmount.amount -
      checkout.usedPoint.amount;
    return calculatedGrandTotal;
  };

  const [grandTotal, setGrandTotal] = React.useState(0);

  const refreshCheckout = (checkout: checkoutType) => {
    setCheckout({ ...checkout });
    setGrandTotal(calculateGrandTotal(checkout));
  };

  const { data, loading: loadingCheckout, refetch } = useQuery<checkoutQuery>(CHECKOUT, {
    fetchPolicy: 'no-cache',
    variables: {
      forMgs: JSON.parse(localStorage.getItem('recentlyAddedProductType')) === 'mgs' ? true : false,
    },
    onCompleted: (data) => {
      const messages = data?.checkout?.messages || [];
      if (0 < messages.length) {
        updateErrorMessages(messages);
      }
      refreshCheckout(data.checkout);
    },
  });

  const [updateCheckout, { loading: loadingUpdate }] = useMutation<checkoutUpdate>(CHECKOUT_UPDATE, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.checkoutUpdate?.checkout?.messages || data?.checkoutUpdate?.errors) {
        const errorMessages = mergeMessagesAndErrors(
          data?.checkoutUpdate?.checkout?.messages,
          data?.checkoutUpdate?.errors,
        );
        updateErrorMessages(errorMessages);
      }
      refreshCheckout(data.checkoutUpdate.checkout);
    },
  });

  const [meEmailVerified] = useLazyQuery<meEmailVerified>(ME_EMAIL_VERIFIED, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const emailVerified = !!data?.me?.emailVerified;

      // local Storage user 정보 업데이트
      const copyCurrentUser: tokenCreate_tokenCreate = JSON.parse(JSON.stringify(currentUser));
      copyCurrentUser.user.emailVerified = emailVerified;

      setCurrentUser(copyCurrentUser);
    },
  });

  useEffect(() => {
    if (currentUser?.user?.emailVerified === undefined || currentUser?.user?.emailVerified === false) {
      meEmailVerified();
    }
  }, []);

  const initialVariables = {
    checkoutId: checkout?.id,
    input: {
      usedPoint: 0,
      voucherCode: '',
    },
  };
  const { freebies } = iterateCartLines<checkout_subcarts_lines>(checkout);

  const [note, setNote] = useState('');

  const [completeCheckout, { loading: loadingComplete }] = useMutation<checkoutComplete>(CHECKOUT_COMPLETE, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const graphqlError = data?.checkoutComplete?.errors || [];
      const errorMessages = mergeMessagesAndErrors(data?.checkoutComplete?.cart?.messages, graphqlError);
      updateErrorMessages(errorMessages);
      if (!shouldStop(errorMessages)) {
        setCurrentCart(data?.checkoutComplete?.cart);

        // 결제 완료 후, 해당 cart의 quantity가 0이고 반대 타입 cart에 상품이 있으면 cart 타입 변경
        if (data?.checkoutComplete?.cart?.quantity === 0) {
          const recentlyAddedProductType = JSON.parse(localStorage.getItem('recentlyAddedProductType'));
          const otherCartQuantity =
            recentlyAddedProductType === 'mgs' ? currentCart?.maccaronCart?.quantity : currentCart?.mgsCart?.quantity;

          if (otherCartQuantity > 0) {
            localStorage.setItem(
              'recentlyAddedProductType',
              JSON.stringify(recentlyAddedProductType === 'mgs' ? 'maccaron' : 'mgs'),
            );
          }
        }

        const order = data?.checkoutComplete?.order;
        const orderToken = order?.token;

        if (data?.checkoutComplete?.order?.paymentType !== OrderPaymentType.COD) {
          pixelInitiateCheckout(order, 'INR');
          history.push(`${orderUrl}${orderToken}/`);
        } else {
          // GA ecommerce (COD)
          pixelPurchase(order, 'INR');
          gaV4Purchase(order);
          history.push({
            pathname: `${orderUrl}${orderToken}/checkout-success/`,
            state: order,
          });
        }
      }
    },
  });

  const [showMobileVerification, setShowMobileVerification] = useState(false);

  const [userMobile, { data: userMobileData }] = useLazyQuery<userMobile>(USER_MOBILE, {
    fetchPolicy: 'no-cache',
    onCompleted: (userMobileData) => {
      const me = userMobileData?.me;
      if (me.mobileNumber === 9999999999 || me.mobileVerified === false) {
        // 인증 안됨 Mobile 인증 시작
        setShowMobileVerification(true);
      } else if (data?.checkout?.isSelectedMgs) {
        setShowMGSDeliveryModal(true);
      } else {
        completeCheckout({ variables: { checkoutId: checkout.id, note } });
      }
    },
  });

  const handleClickContinue = (e: any) => {
    if (isInvalidShippingAddress(checkout?.shippingAddress)) {
      showSnackbar({ message: 'Shipping address is invalid. Please check your address and try again.' });

      return;
    }

    // Mobile 인증 여부 확인
    if (
      currentUser.user.mobileNumber === 9999999999 ||
      (currentUser.user['mobileVerified'] !== undefined && currentUser.user.mobileVerified === false)
    ) {
      // Mobile 인증
      // user 정보 불러와서 다시 한번 확인하기 (mobileNumber, mobileVerified 만 쿼리하기)
      userMobile();
    } else {
      if (data?.checkout?.isSelectedMgs) {
        setShowMGSDeliveryModal(true);
      } else {
        completeCheckout({ variables: { checkoutId: checkout.id, note } });
      }
    }
  };

  const forceCheckout = () => {
    setShowMobileVerification(false);

    if (data?.checkout?.isSelectedMgs) {
      setShowMGSDeliveryModal(true);
    } else {
      completeCheckout({ variables: { checkoutId: checkout.id, note } });
    }
  };

  const updateErrorMessages = (errors: CheckoutError[]) => {
    for (const e of errors) {
      if (e.message.includes(RESOLVE_ERROR_MESSAGE)) {
        e.message = 'Invalid checkout. Please refresh this page.';
      }
    }
    setErrorMessages(errors);
  };

  const shouldStop = (errors: CheckoutError[]) => {
    return errors.some((e) => {
      return e['level'] === 'danger' || e['level'] === 'error';
    });
  };

  const [errorMessages, setErrorMessages] = React.useState([]);
  const [disabledContinueButton, setDisabledContinueButton] = useState(false);
  useEffect(() => {
    if (shouldStop(errorMessages)) {
      setDisabledContinueButton(true);
    } else {
      setDisabledContinueButton(false);
    }
  }, [JSON.stringify(errorMessages)]);

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(loadingCheckout || loadingUpdate || loadingComplete);
  }, [loadingCheckout, loadingUpdate, loadingComplete]);

  const [hasSentGA, setHasSentGA] = useState(false);

  useEffect(() => {
    if (!hasSentGA && data) {
      const c = data.checkout;
      const value = c?.totalPrice?.gross?.amount;
      const items = gaV4GetItemsFromCart(c);
      gaV4BeginCheckout(items, value);
      setHasSentGA(true);
    }
  }, [JSON.stringify(data)]);

  const handleOtpVerifyClose = () => {
    setShowMobileVerification(false);
  };

  return checkout && 0 < checkout.quantity ? (
    <S.Wrapper>
      <OtpVerificationModal
        show={showMobileVerification}
        onHide={handleOtpVerifyClose}
        closeNotifyMe={false}
        forceCheckout={forceCheckout}
      />
      <Container>
        <Row>
          <Col>
            <S.Header>
              <ImageButton
                href={baseUrl}
                imgSrc={isComDomain ? GlobalStoreLogo : Logo}
                width={isComDomain ? '250' : '150'}
              />
            </S.Header>
          </Col>
        </Row>
      </Container>
      <Container>
        <CheckoutAlert errorMessages={errorMessages} />
        <Row>
          <Col md={8} xs={12}>
            <CheckoutInformation
              checkout={checkout}
              freebies={freebies}
              updateCheckout={updateCheckout}
              initialVariables={initialVariables}
              setCheckout={setCheckout}
              setNote={setNote}
              loading={loading}
              grandTotal={grandTotal}
              refetch={refetch}
            />
          </Col>
          <Col md={4} xs={12}>
            <CheckoutSummary
              originalTotal={checkout.originalTotal}
              codAmount={checkout.codAmount}
              shippingPrice={checkout.shippingPrice}
              usedPoint={checkout.usedPoint}
              discountAmount={checkout.discountAmount}
              totalPrice={checkout.totalPrice}
              voucherCode={checkout.voucherCode}
              handleClickContinue={handleClickContinue}
              grandTotal={grandTotal}
              estimatedEarnPoint={checkout.estimatedEarnPoint.amount}
              disabled={loading || disabledContinueButton}
            />
          </Col>
        </Row>
      </Container>

      {!currentUser?.user?.emailVerified ? (
        <Container className="email-verification-alert-wrapper">
          <Alert
            variant="outlined"
            severity="warning"
            action={
              <Button size="small" onClick={() => history.push(emailVerifyRequestUrl)}>
                Verify
              </Button>
            }
          >
            <Typography variant="subtitle2" component="p">
              Email Verification Required
            </Typography>
          </Alert>
        </Container>
      ) : null}

      <MGSDeliveryModal
        open={showMGSDeliveryModal}
        onClose={() => setShowMGSDeliveryModal(false)}
        onConfirm={() => {
          setShowMGSDeliveryModal(false);
          completeCheckout({ variables: { checkoutId: checkout.id, note } });
        }}
      />
    </S.Wrapper>
  ) : (
    //TODO: show empty checkout or redirect if empty
    <Loading />
  );
};

export { CheckoutPage };
